// @ts-nocheck
import React, {useContext} from "react";
import {ResponsiveBar} from "@nivo/bar";
import ChartCard from "./ChartCard";
import StyleContext from "../../../contexts/StyleContext";
import {
  FIFA_PALETTE,
  WIN_RATE_AXIS,
  coinFlipMarker,
  getNivoTheme
} from "./chartTheme";

// Shared vertical bar for win-rate breakdowns (4.2, 4.3, 4.6). Fixes the shared
// conventions — 0-100 axis, 50% coin-flip marker, value% labels, themed tooltip
// — so each chart only supplies its data, labels, and extra tooltip rows.

export interface TooltipRow {
  label: string;
  value: React.ReactNode;
}

export interface WinRateBarProps {
  title: string;
  takeaway: string;
  /** Rows keyed by `indexBy`, each with a numeric `winRate`. */
  data: Array<Record<string, unknown>>;
  /** Field used for the category axis (e.g. "year", "stage", "type"). */
  indexBy: string;
  /** Optional formatter for the category tick + tooltip title. */
  formatIndex?: (value: string) => string;
  axisBottomLegend?: string;
  /** Extra tooltip rows (beyond win rate), derived from the raw datum. */
  tooltipRows?: (datum: Record<string, unknown>) => TooltipRow[];
  /** Nivo `colors` config; defaults to the primary palette color. */
  colors?: unknown;
  footnote?: React.ReactNode;
}

export default function WinRateBar({
  title,
  takeaway,
  data,
  indexBy,
  formatIndex,
  axisBottomLegend,
  tooltipRows,
  colors,
  footnote
}: WinRateBarProps) {
  const {isDark} = useContext(StyleContext);

  const BarTooltip = ({indexValue, value, data: raw}) => {
    const extraRows = tooltipRows ? tooltipRows(raw) : [];
    return (
      <div className="fifa-tooltip">
        <span className="fifa-tooltip__title">
          {formatIndex ? formatIndex(indexValue) : indexValue}
        </span>
        <span className="fifa-tooltip__row">
          <span>Win rate</span>
          <span>{value}%</span>
        </span>
        {extraRows.map(row => (
          <span className="fifa-tooltip__row" key={row.label}>
            <span>{row.label}</span>
            <span>{row.value}</span>
          </span>
        ))}
      </div>
    );
  };

  return (
    <ChartCard title={title} takeaway={takeaway} footnote={footnote}>
      <ResponsiveBar
        data={data}
        keys={["winRate"]}
        indexBy={indexBy}
        margin={{top: 20, right: 24, bottom: 56, left: 60}}
        padding={0.3}
        valueScale={{type: "linear"}}
        minValue={0}
        maxValue={WIN_RATE_AXIS.max}
        colors={colors || [FIFA_PALETTE.primary]}
        colorBy="indexValue"
        borderRadius={3}
        enableLabel={true}
        label={d => `${d.value}%`}
        labelSkipHeight={16}
        labelTextColor="#ffffff"
        axisBottom={{
          legend: axisBottomLegend,
          legendPosition: "middle",
          legendOffset: 44,
          format: formatIndex
        }}
        axisLeft={{
          legend: WIN_RATE_AXIS.label,
          legendPosition: "middle",
          legendOffset: -46,
          tickValues: [0, 25, 50, 75, 100]
        }}
        gridYValues={[0, 25, 50, 75, 100]}
        markers={[coinFlipMarker(isDark)]}
        tooltip={BarTooltip}
        theme={getNivoTheme(isDark)}
        animate={true}
        motionConfig="gentle"
        role="img"
      />
    </ChartCard>
  );
}
